import React, { useState, useMemo, useCallback, useEffect } from 'react';
import {
    Button,
    IconButton,
    Box,
    Typography,
    TextField,
    Autocomplete,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    Paper,
    Tabs,
    Tab,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DropzoneArea from '../DropzoneArea';
import EnumsService from '../../../../Services/enums.service';
import { toast } from 'react-toastify';

const EasyCountImportModal = ({
    open,
    onClose,
    caseId,
    accountantId,
    token,
    onSuccess,
    previewEasyCountClients,
    importEasyCountClients,
    previewEasyCountReport,
    importEasyCountReport,
    sortCodeIdOptions = [],
    accountCardOptions: accountCardOptionsProp = [],
    client,
    setAccountCardOptions,
    fetchEnums,
    t,
    direction = 'rtl',
}) => {
    const [tabValue, setTabValue] = useState(0);

    const [clientsFile, setClientsFile] = useState(null);
    const [clientsPreviewData, setClientsPreviewData] = useState(null);
    const [clientsProcessing, setClientsProcessing] = useState(false);

    const [reportFile, setReportFile] = useState(null);
    const [reportPreviewData, setReportPreviewData] = useState(null);
    const [reportProcessing, setReportProcessing] = useState(false);
    const [defaultSortCodeId, setDefaultSortCodeId] = useState('100');
    const [defaultDebitAccountId, setDefaultDebitAccountId] = useState('4');
    const [paymentSortCodeId, setPaymentSortCodeId] = useState('3');
    const [paymentTypeToCardId, setPaymentTypeToCardId] = useState({});
    const [reportPage, setReportPage] = useState(0);
    const [reportRowsPerPage, setReportRowsPerPage] = useState(10);

    const [localAccountCardOptions, setLocalAccountCardOptions] = useState([]);
    const effectiveCaseId = client?.caseId ?? caseId;

    const accountCardOptions = localAccountCardOptions.length > 0 ? localAccountCardOptions : accountCardOptionsProp;
    const cardsWithoutAddItem = useMemo(
        () => (accountCardOptions || []).filter((c) => c.value1 !== 'AddItem'),
        [accountCardOptions]
    );
    const debitOptions = useMemo(
        () =>
            cardsWithoutAddItem.map((c) => ({
                ...c,
                cardId: c.cardId ?? c.value1,
                label: `${c.name ?? ''} - ${c.cardId ?? ''}`.trim(),
            })),
        [cardsWithoutAddItem]
    );

    const cardIdToName = useMemo(() => {
        const m = new Map();
        (cardsWithoutAddItem || []).forEach((o) => {
            const id = o.cardId ?? o.value1 ?? o.value;
            if (id == null || id === '') return;
            const strId = String(id).trim();
            const name = String(o.name ?? o.label ?? o.accountCaseId ?? '').trim() || strId;
            m.set(strId, name);
            if (/^\d+$/.test(strId)) m.set(Number(strId), name);
        });
        return m;
    }, [cardsWithoutAddItem]);

    useEffect(() => {
        if (!open || !effectiveCaseId || !token) return;
        let cancelled = false;
        (async () => {
            try {
                const res = await EnumsService.GetAccountCardsByCaseId(effectiveCaseId, token);
                if (cancelled || !res?.ok) return;
                const data = await res.json();
                setLocalAccountCardOptions([{ name: t('AddAccountCard'), value1: 'AddItem' }, ...(data || [])]);
                if (setAccountCardOptions)
                    setAccountCardOptions([{ name: t('AddAccountCard'), value1: 'AddItem' }, ...(data || [])]);
            } catch (e) {
                if (!cancelled) console.error('Fetch account cards failed:', e);
            }
        })();
        return () => { cancelled = true; };
    }, [open, effectiveCaseId, token, t, setAccountCardOptions]);

    const formatAmount = (amount) => {
        if (amount == null) return '';
        return new Intl.NumberFormat('he-IL', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        }).format(amount);
    };

    const formatClientDisplay = useCallback(
        (clientName, clientTaxId, creditAccountName, creditAccount) => {
            const name = clientName || creditAccountName ||
                (creditAccount ? cardIdToName.get(String(creditAccount).trim()) || cardIdToName.get(Number(creditAccount)) : null);
            const id = clientTaxId || creditAccount || '';
            if (name && id) return `${name} (${id})`;
            return name || id || '—';
        },
        [cardIdToName]
    );

    const handleClose = () => {
        setTabValue(0);
        setClientsFile(null);
        setClientsPreviewData(null);
        setReportFile(null);
        setReportPreviewData(null);
        setPaymentTypeToCardId({});
        setReportPage(0);
        onClose();
    };

    const runClientsPreview = useCallback(async () => {
        if (!clientsFile || !effectiveCaseId || !accountantId || !token || !previewEasyCountClients) return;
        try {
            setClientsProcessing(true);
            const res = await previewEasyCountClients(effectiveCaseId, accountantId, clientsFile, token);
            setClientsPreviewData({
                entries: res.entries ?? [],
                totalRows: res.totalRows ?? 0,
                validCount: res.validCount ?? 0,
                errorCount: res.errorCount ?? 0,
                errorMessages: res.errorMessages ?? [],
            });
        } catch (err) {
            console.error('EasyCount clients preview failed:', err);
            setClientsPreviewData({
                entries: [],
                totalRows: 0,
                validCount: 0,
                errorCount: 1,
                errorMessages: [err?.message || 'Preview failed'],
            });
        } finally {
            setClientsProcessing(false);
        }
    }, [clientsFile, effectiveCaseId, accountantId, token, previewEasyCountClients]);

    const handleClientsImport = async () => {
        if (!clientsFile || !effectiveCaseId || !token || !importEasyCountClients) return;
        try {
            setClientsProcessing(true);
            const res = await importEasyCountClients(effectiveCaseId, clientsFile, token);
            const processed = res?.processedCount ?? 0;
            const errors = res?.errorCount ?? 0;
            if (errors > 0) {
                toast.warning(`${t('ImportSuccessful')} – ${processed} ${t('processed')}. ${t('Errors')}: ${errors}`);
            } else {
                toast.success(processed > 0 ? `${t('ImportSuccessful')} – ${processed} ${t('processed')}` : t('ImportSuccessful'));
            }
            if (fetchEnums) await fetchEnums();
            onSuccess?.();
            handleClose();
        } catch (err) {
            console.error('EasyCount clients import failed:', err);
            toast.error(t('ImportFailed') || err?.message || 'Import failed');
        } finally {
            setClientsProcessing(false);
        }
    };

    const runReportPreview = useCallback(async () => {
        if (!reportFile || !effectiveCaseId || !accountantId || !token || !previewEasyCountReport) return;
        try {
            setReportProcessing(true);
            const res = await previewEasyCountReport(effectiveCaseId, accountantId, reportFile, token);
            setReportPreviewData({
                entries: res.entries ?? [],
                distinctPaymentTypes: res.distinctPaymentTypes ?? [],
                totalRows: res.totalRows ?? 0,
                validCount: res.validCount ?? 0,
                errorCount: res.errorCount ?? 0,
                errorMessages: res.errorMessages ?? [],
            });
            setReportPage(0);
            setPaymentTypeToCardId((prev) => {
                const next = { ...prev };
                (res.distinctPaymentTypes ?? []).forEach((pt) => {
                    if (next[pt] == null) next[pt] = '';
                });
                return next;
            });
        } catch (err) {
            console.error('EasyCount report preview failed:', err);
            setReportPreviewData({
                entries: [],
                distinctPaymentTypes: [],
                totalRows: 0,
                validCount: 0,
                errorCount: 1,
                errorMessages: [err?.message || 'Preview failed'],
            });
        } finally {
            setReportProcessing(false);
        }
    }, [reportFile, effectiveCaseId, accountantId, token, previewEasyCountReport]);

    const handleReportImport = async () => {
        if (!reportFile || !effectiveCaseId || !token || !importEasyCountReport) return;
        const distinctTypes = reportPreviewData?.distinctPaymentTypes ?? [];
        const allMapped = distinctTypes.length === 0 || distinctTypes.every((pt) => paymentTypeToCardId[pt]);
        if (!allMapped) {
            toast.warning(t('MapAllPaymentTypes') || 'Please map a debit account for each payment type.');
            return;
        }
        const mapping = {};
        distinctTypes.forEach((pt) => {
            const cardId = paymentTypeToCardId[pt];
            if (cardId) mapping[pt] = cardId;
        });
        try {
            setReportProcessing(true);
            const res = await importEasyCountReport(
                effectiveCaseId,
                reportFile,
                defaultSortCodeId || '100',
                defaultDebitAccountId || '4',
                paymentSortCodeId || '3',
                mapping,
                token
            );
            const processed = res?.processedCount ?? 0;
            const errors = res?.errorCount ?? 0;
            const duplicateCount = res?.duplicateCount ?? 0;
            const duplicateMessages = res?.duplicateMessages ?? [];
            if (errors > 0 && (res?.errorMessages ?? []).length > 0) {
                toast.error(`${t('ImportFailed')} – ${t('processed')}: ${processed}, ${t('Errors')}: ${errors}`);
            } else if (errors > 0) {
                toast.warning(`${t('ImportSuccessful')} – ${processed} ${t('processed')}. ${t('Errors')}: ${errors}`);
            } else {
                let msg = processed > 0 ? `${t('ImportSuccessful')} – ${processed} ${t('processed')}` : t('ImportSuccessful');
                if (duplicateCount > 0) {
                    msg += `. ${duplicateCount} ${t('DuplicatesSkipped') || 'duplicates skipped'}`;
                    duplicateMessages.slice(0, 3).forEach((m) => toast.info(m));
                }
                toast.success(msg);
            }
            onSuccess?.();
            handleClose();
        } catch (err) {
            console.error('EasyCount report import failed:', err);
            toast.error(t('ImportFailed') || err?.message || 'Import failed');
        } finally {
            setReportProcessing(false);
        }
    };

    if (!open) return null;

    const reportEntries = reportPreviewData?.entries ?? [];
    const distinctPaymentTypes = reportPreviewData?.distinctPaymentTypes ?? [];
    const allPaymentTypesMapped = distinctPaymentTypes.length === 0 || distinctPaymentTypes.every((pt) => paymentTypeToCardId[pt]);

    const renderSortCodeAutocomplete = (value, onChange, label) =>
        sortCodeIdOptions?.length > 0 ? (
            <Autocomplete
                options={sortCodeIdOptions}
                getOptionLabel={(o) => `${o.sortCodeId} - ${o.sortCodeName ?? ''}`}
                value={sortCodeIdOptions.find((o) => String(o.sortCodeId) === String(value)) || null}
                onChange={(_, v) => onChange(v ? String(v.sortCodeId) : value)}
                size="small"
                sx={{ minWidth: 200 }}
                renderInput={(params) => <TextField {...params} label={label} variant="standard" />}
            />
        ) : (
            <TextField
                label={label}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                size="small"
                sx={{ minWidth: 120 }}
                variant="standard"
            />
        );

    return (
        <div className="download-modal-overlay" onClick={handleClose}>
            <div className="download-modal" onClick={(e) => e.stopPropagation()}>
                <div
                    className={`download-modal-content ${direction}`}
                    style={{ position: 'relative', maxWidth: '1100px' }}
                >
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                        <h4 className="report-title">
                            {t('EasyCountCsvImport') || 'EasyCount CSV'} – {t('Preview')} & {t('Import')}
                        </h4>
                        <IconButton className="client-back-icon" onClick={handleClose} sx={{ padding: '4px' }}>
                            <CloseIcon />
                        </IconButton>
                    </Box>

                    <Tabs value={tabValue} onChange={(_, v) => setTabValue(v)} sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
                        <Tab label={t('EasyCountClients') || 'Clients'} />
                        <Tab label={t('EasyCountReport') || 'Report'} />
                    </Tabs>

                    {tabValue === 0 && (
                        <Box>
                            <div>
                                <h5>.csv ({t('EasyCountClients') || 'Clients / contacts'})</h5>
                                <DropzoneArea
                                    key={clientsFile ? clientsFile.name : 'easycount-clients-empty'}
                                    onDrop={(files) => {
                                        if (files?.length) setClientsFile(files[0]);
                                        setClientsPreviewData(null);
                                    }}
                                    accept=".csv"
                                />
                                {clientsFile && (
                                    <Typography variant="body2" sx={{ mt: 1 }}>
                                        {t('FileSelected')}: {clientsFile.name}
                                    </Typography>
                                )}
                            </div>
                            <Box display="flex" gap={1.5} mt={2}>
                                <Button variant="contained" onClick={runClientsPreview} disabled={!clientsFile || clientsProcessing}>
                                    {clientsProcessing && !clientsPreviewData ? t('Processing') : t('Preview')}
                                </Button>
                                <Button
                                    variant="contained"
                                    color="success"
                                    onClick={handleClientsImport}
                                    disabled={!clientsPreviewData || clientsProcessing || (clientsPreviewData?.validCount ?? 0) === 0}
                                >
                                    {clientsProcessing && clientsPreviewData ? t('Processing') : t('Import')}
                                </Button>
                            </Box>
                            {clientsPreviewData && (
                                <Box mt={3}>
                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                        {t('TotalLines') || 'Total lines'}: <strong>{clientsPreviewData.totalRows}</strong>
                                        {' | '}
                                        {t('ValidCount') || 'Valid'}: <strong>{clientsPreviewData.validCount}</strong>
                                        {' | '}
                                        {t('ErrorCount') || 'Errors'}: <strong>{clientsPreviewData.errorCount}</strong>
                                    </Typography>
                                    {clientsPreviewData.errorMessages?.length > 0 && (
                                        <Box mb={2} p={1.5} bgcolor="error.light" borderRadius={1} sx={{ maxHeight: 220, overflow: 'auto' }}>
                                            <Typography variant="subtitle2" color="error.dark" sx={{ mb: 0.5 }}>
                                                {t('Errors') || 'Errors'} ({clientsPreviewData.errorMessages.length})
                                            </Typography>
                                            <Box component="ul" sx={{ m: 0, pl: 2 }}>
                                                {clientsPreviewData.errorMessages.map((msg, i) => (
                                                    <li key={i}>
                                                        <Typography variant="body2" component="span" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                                                            {msg}
                                                        </Typography>
                                                    </li>
                                                ))}
                                            </Box>
                                        </Box>
                                    )}
                                    {clientsPreviewData.entries?.length > 0 && (
                                        <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 400, overflowX: 'auto' }}>
                                            <Table size="small" stickyHeader>
                                                <TableHead>
                                                    <TableRow>
                                                        <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>{t('Line')}</TableCell>
                                                        <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>{t('Name') || 'Name'}</TableCell>
                                                        <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>{t('TaxId') || 'Tax ID'}</TableCell>
                                                        <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>{t('Email')}</TableCell>
                                                        <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>{t('Phone') || 'Phone'}</TableCell>
                                                        <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>{t('Status')}</TableCell>
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {clientsPreviewData.entries.map((row, i) => (
                                                        <TableRow
                                                            key={row.lineNumber ?? i}
                                                            sx={{ bgcolor: row.isValid ? 'transparent' : 'action.hover' }}
                                                        >
                                                            <TableCell sx={{ fontSize: 12 }}>{row.lineNumber}</TableCell>
                                                            <TableCell sx={{ fontSize: 12 }}>{row.name ?? '—'}</TableCell>
                                                            <TableCell sx={{ fontSize: 12 }}>{row.taxRegNumber ?? '—'}</TableCell>
                                                            <TableCell sx={{ fontSize: 12 }}>{row.email ?? '—'}</TableCell>
                                                            <TableCell sx={{ fontSize: 12 }}>{row.phone ?? '—'}</TableCell>
                                                            <TableCell sx={{ fontSize: 12, maxWidth: 320, whiteSpace: 'normal', wordBreak: 'break-word' }}>
                                                                {row.isValid ? (
                                                                    <Typography variant="body2" color="success.main">{t('Valid') || 'OK'}</Typography>
                                                                ) : (
                                                                    <Typography variant="body2" color="error.main" title={row.errorMessage ?? ''}>{row.errorMessage ?? ''}</Typography>
                                                                )}
                                                            </TableCell>
                                                        </TableRow>
                                                    ))}
                                                </TableBody>
                                            </Table>
                                        </TableContainer>
                                    )}
                                </Box>
                            )}
                        </Box>
                    )}

                    {tabValue === 1 && (
                        <Box>
                            <div>
                                <h5>.csv ({t('EasyCountReport') || 'Report'})</h5>
                                <DropzoneArea
                                    key={reportFile ? reportFile.name : 'easycount-report-empty'}
                                    onDrop={(files) => {
                                        if (files?.length) setReportFile(files[0]);
                                        setReportPreviewData(null);
                                        setPaymentTypeToCardId({});
                                    }}
                                    accept=".csv"
                                />
                                {reportFile && (
                                    <Typography variant="body2" sx={{ mt: 1 }}>
                                        {t('FileSelected')}: {reportFile.name}
                                    </Typography>
                                )}
                            </div>
                            <Box display="flex" flexWrap="wrap" gap={2} mt={2}>
                                {renderSortCodeAutocomplete(
                                    defaultSortCodeId,
                                    setDefaultSortCodeId,
                                    t('DefaultSortCode') || 'Income sort code'
                                )}
                                {renderSortCodeAutocomplete(
                                    paymentSortCodeId,
                                    setPaymentSortCodeId,
                                    t('PaymentSortCode') || 'Payment sort code'
                                )}
                                <Autocomplete
                                    options={debitOptions}
                                    getOptionLabel={(o) => o.label || o.cardId || ''}
                                    value={debitOptions.find((o) => String(o.cardId) === String(defaultDebitAccountId)) || null}
                                    onChange={(_, v) => setDefaultDebitAccountId(v ? String(v.cardId) : '4')}
                                    size="small"
                                    sx={{ minWidth: 220 }}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label={t('DefaultCreditAccount') || 'Income credit account'}
                                            variant="standard"
                                        />
                                    )}
                                />
                            </Box>
                            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                                {t('EasyCountTypeHints') ||
                                    'חשבונית מס / חשבונית זיכוי: income sort + credit account. קבלה: payment sort + payment mapping. חשבונית מס קבלה: both.'}
                            </Typography>
                            <Box display="flex" gap={1.5} mt={2}>
                                <Button variant="contained" onClick={runReportPreview} disabled={!reportFile || reportProcessing}>
                                    {reportProcessing && !reportPreviewData ? t('Processing') : t('Preview')}
                                </Button>
                                <Button
                                    variant="contained"
                                    color="success"
                                    onClick={handleReportImport}
                                    disabled={
                                        !reportPreviewData ||
                                        reportProcessing ||
                                        !allPaymentTypesMapped ||
                                        (reportPreviewData?.validCount ?? 0) === 0
                                    }
                                >
                                    {reportProcessing && reportPreviewData ? t('Processing') : t('Import')}
                                </Button>
                            </Box>
                            {reportPreviewData && distinctPaymentTypes.length > 0 && (
                                <Box mt={2} p={1.5} bgcolor="grey.50" borderRadius={1}>
                                    <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700 }}>
                                        {t('PaymentTypeMapping') || 'Payment type → Debit account'}
                                    </Typography>
                                    <Box display="flex" flexWrap="wrap" gap={2}>
                                        {distinctPaymentTypes.map((pt) => (
                                            <Autocomplete
                                                key={pt}
                                                options={debitOptions}
                                                getOptionLabel={(o) => o.label || o.cardId || ''}
                                                value={debitOptions.find((o) => String(o.cardId) === String(paymentTypeToCardId[pt] ?? '')) || null}
                                                onChange={(_, v) => setPaymentTypeToCardId((prev) => ({ ...prev, [pt]: v ? String(v.cardId) : '' }))}
                                                size="small"
                                                sx={{ minWidth: 220 }}
                                                renderInput={(params) => <TextField {...params} label={pt} variant="outlined" />}
                                            />
                                        ))}
                                    </Box>
                                </Box>
                            )}
                            {reportPreviewData && reportEntries.length > 0 && (
                                <Box mt={3} sx={{ maxHeight: 600, overflow: 'auto' }}>
                                    {(() => {
                                        const validEntries = reportEntries.filter((e) => e.isValid);
                                        const incomeEntries = validEntries.filter((e) => e.journalType === 'Income');
                                        const paymentEntries = validEntries.filter((e) => e.journalType === 'Payment');
                                        const sumIncome = incomeEntries.reduce((s, e) => s + (Number(e.amount) || 0), 0);
                                        const sumPayment = paymentEntries.reduce((s, e) => s + (Number(e.amount) || 0), 0);
                                        return (
                                            <>
                                                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                                    {t('TotalLines') || 'Total lines'}: <strong>{reportPreviewData.totalRows}</strong>
                                                    {' | '}
                                                    {t('ValidCount') || 'Valid'}: <strong>{reportPreviewData.validCount}</strong>
                                                    {' | '}
                                                    {t('ErrorCount') || 'Errors'}: <strong>{reportPreviewData.errorCount}</strong>
                                                </Typography>
                                                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                                    {t('ICountIncome') || 'Income'}: <strong>{formatAmount(sumIncome)}</strong>
                                                    {' | '}
                                                    {t('ICountPayment') || 'Payment'}: <strong>{formatAmount(sumPayment)}</strong>
                                                    {' | '}
                                                    {t('ICountJournalEntriesToCreate') || 'Journal entries'}: <strong>{validEntries.length}</strong>
                                                    {' '}({t('ICountIncome')}: {incomeEntries.length}, {t('ICountPayment')}: {paymentEntries.length})
                                                </Typography>
                                                {reportPreviewData.errorMessages?.length > 0 && (
                                                    <Box mb={2} p={1.5} bgcolor="error.light" borderRadius={1} sx={{ maxHeight: 220, overflow: 'auto' }}>
                                                        <Typography variant="subtitle2" color="error.dark" sx={{ mb: 0.5 }}>
                                                            {t('Errors') || 'Errors'} ({reportPreviewData.errorMessages.length})
                                                        </Typography>
                                                        <Box component="ul" sx={{ m: 0, pl: 2 }}>
                                                            {reportPreviewData.errorMessages.map((msg, i) => (
                                                                <li key={i}>
                                                                    <Typography variant="body2" component="span" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                                                                        {msg}
                                                                    </Typography>
                                                                </li>
                                                            ))}
                                                        </Box>
                                                    </Box>
                                                )}
                                                <TableContainer component={Paper} variant="outlined" sx={{ overflowX: 'auto' }}>
                                                    <Table size="small" stickyHeader>
                                                        <TableHead>
                                                            <TableRow>
                                                                <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>{t('Line')}</TableCell>
                                                                <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>{t('DocumentType') || 'Doc type'}</TableCell>
                                                                <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>{t('ICountJournalType') || 'Type'}</TableCell>
                                                                <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>{t('DocumentDate')}</TableCell>
                                                                <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>{t('Reference')}</TableCell>
                                                                <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>{t('Client')}</TableCell>
                                                                <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>{t('PaymentType')}</TableCell>
                                                                <TableCell align="right" sx={{ fontWeight: 700, fontSize: 12 }}>{t('Amount')}</TableCell>
                                                                <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>{t('Status')}</TableCell>
                                                            </TableRow>
                                                        </TableHead>
                                                        <TableBody>
                                                            {reportEntries
                                                                .slice(reportPage * reportRowsPerPage, reportPage * reportRowsPerPage + reportRowsPerPage)
                                                                .map((row, i) => (
                                                                    <TableRow
                                                                        key={`${row.lineNumber}-${row.journalType}-${row.paymentType ?? ''}-${i}`}
                                                                        sx={{ bgcolor: row.isValid ? 'transparent' : 'action.hover' }}
                                                                    >
                                                                        <TableCell sx={{ fontSize: 12 }}>{row.lineNumber}</TableCell>
                                                                        <TableCell sx={{ fontSize: 12 }}>{row.documentType ?? '—'}</TableCell>
                                                                        <TableCell sx={{ fontSize: 12 }}>
                                                                            <Typography
                                                                                variant="body2"
                                                                                fontWeight={row.journalType === 'Payment' ? 600 : 400}
                                                                                color={row.journalType === 'Payment' ? 'primary.main' : 'text.primary'}
                                                                            >
                                                                                {row.journalType === 'Payment'
                                                                                    ? (t('ICountPayment') || 'Payment')
                                                                                    : (t('ICountIncome') || 'Income')}
                                                                            </Typography>
                                                                        </TableCell>
                                                                        <TableCell sx={{ fontSize: 12 }}>{row.documentDate ?? '—'}</TableCell>
                                                                        <TableCell sx={{ fontSize: 12 }}>{row.reference ?? '—'}</TableCell>
                                                                        <TableCell sx={{ fontSize: 12 }}>
                                                                            {formatClientDisplay(row.clientName, row.clientTaxId, row.creditAccountName, row.creditAccount)}
                                                                        </TableCell>
                                                                        <TableCell sx={{ fontSize: 12 }}>{row.paymentType ?? '—'}</TableCell>
                                                                        <TableCell align="right" sx={{ fontSize: 12 }}>{formatAmount(row.amount)}</TableCell>
                                                                        <TableCell sx={{ fontSize: 12, maxWidth: 320, whiteSpace: 'normal', wordBreak: 'break-word' }}>
                                                                            {row.isValid ? (
                                                                                <Typography variant="body2" color="success.main">{t('Valid') || 'OK'}</Typography>
                                                                            ) : (
                                                                                <Typography variant="body2" color="error.main" title={row.errorMessage ?? ''}>{row.errorMessage ?? ''}</Typography>
                                                                            )}
                                                                        </TableCell>
                                                                    </TableRow>
                                                                ))}
                                                        </TableBody>
                                                    </Table>
                                                    <TablePagination
                                                        component="div"
                                                        count={reportEntries.length}
                                                        page={reportPage}
                                                        onPageChange={(_, p) => setReportPage(p)}
                                                        rowsPerPage={reportRowsPerPage}
                                                        onRowsPerPageChange={(e) => {
                                                            setReportRowsPerPage(parseInt(e.target.value, 10));
                                                            setReportPage(0);
                                                        }}
                                                        rowsPerPageOptions={[10, 25, 50]}
                                                        labelRowsPerPage={t('RowsPerPage') || 'Rows per page'}
                                                    />
                                                </TableContainer>
                                            </>
                                        );
                                    })()}
                                </Box>
                            )}
                        </Box>
                    )}
                </div>
            </div>
        </div>
    );
};

export default EasyCountImportModal;
